import React from 'react'
import PropTypes from 'prop-types'
import { Alert, Card, Empty, Space, Steps, Tag, Typography } from 'antd'
import { StageTag } from './tags'
import { formatDateTime, isEnded, journeySummary } from './utils'

// Which antd Steps state each server state means. The server owns the journey; this only paints it.
const STEP_STATUS = { done: 'finish', current: 'process', upcoming: 'wait' }

// The customer's own screen, read-only, so a team member on the phone is describing what the
// person in front of them is looking at rather than a second version of it.
//
// Every read of `eta`, `route` and `vessel` goes through `journeySummary`: the server sends all
// three as null before payment, while starting and while waiting, and `vessel` is null for every
// RoRo shipment. An unguarded read here takes the whole page down.
const CustomerJourney = ({ view }) => {
  if (!view) {
    return (
      <Card title="What the customer sees">
        <Empty description="The customer view is not available." />
      </Card>
    )
  }

  const summary = journeySummary(view)
  const stage = view.stage || {}
  const containers = view.containers || []

  return (
    <Card
      title="What the customer sees"
      extra={<span style={{ opacity: 0.65 }}>Read-only — exactly what the app shows</span>}
    >
      {view.attention ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={view.attention.title}
          description={view.attention.message}
        />
      ) : null}

      <Space align="center" wrap>
        <Typography.Title level={5} style={{ marginBottom: 0 }}>
          {stage.title || '—'}
        </Typography.Title>
        {/* Tracking stopped without a gate-out. The customer is reading "Tracking has ended"; this
            tag tells the team it is not a collection, whatever stage the journey stopped at. */}
        {isEnded(view.completion) ? <Tag>Ended without gate-out</Tag> : null}
      </Space>
      {stage.explanation ? (
        <div style={{ marginTop: 4 }}>{stage.explanation}</div>
      ) : null}

      <div style={{ marginTop: 12 }}>
        {summary.etaText}
        {summary.etaEstimated ? <Tag style={{ marginLeft: 8 }}>estimated</Tag> : null}
      </div>
      {summary.routeText ? <div>{summary.routeText}</div> : null}
      {summary.vesselText ? <div>{summary.vesselText}</div> : null}

      {/* Every step carries its own status from the server, so this never derives one from
          `current`. */}
      <Steps direction="vertical" size="small" style={{ marginTop: 16 }}>
        {(view.stages || []).map(step => (
          <Steps.Step
            key={step.key}
            status={STEP_STATUS[step.state] || 'wait'}
            title={step.title}
            description={
              step.at
                ? `${formatDateTime(step.at)}${step.estimated ? ' (estimated)' : ''}`
                : null
            }
          />
        ))}
      </Steps>

      <div style={{ marginTop: 16 }}>
        {containers.length ? (
          <Space wrap>
            {containers.map(box => (
              <span key={box.number}>
                <Typography.Text code>{box.number}</Typography.Text>{' '}
                <StageTag stage={box.stage} />
              </span>
            ))}
          </Space>
        ) : (
          <span style={{ opacity: 0.65 }}>No containers — tracked by BL or booking</span>
        )}
      </div>

      <div style={{ marginTop: 12, opacity: 0.65 }}>
        {view.lastCheckedAt
          ? `Last checked ${formatDateTime(view.lastCheckedAt)}`
          : 'Not checked yet'}
      </div>
    </Card>
  )
}

CustomerJourney.propTypes = {
  view: PropTypes.object
}

export default CustomerJourney
